import { Trans, useLingui } from '@lingui/react/macro'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { skipToken, useSuspenseQuery } from '@apollo/client'
import { Mail01Icon } from '@hugeicons/core-solid-rounded'

import * as Form from '@/features/form'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import useGuestForm, { FormData } from '../hooks/useGuestForm'
import { useIsConnected } from '@/features/pms'
import { Permission, useCheckPermission } from '@/features/auth'

interface Props {
  id?: number
  defaultName?: string
  onDone?: () => void
}

export function GuestForm({ id, defaultName, onDone }: Props) {
  const { t } = useLingui()

  const hasUpdatePermission = useCheckPermission([Permission.STAY_UPDATE])
  const isConnectedToPms = useIsConnected()
  const canUpdate = hasUpdatePermission && !isConnectedToPms

  const { data } = useSuspenseQuery(
    AdminAPI.guest.GET,
    id
      ? {
          variables: {
            id
          }
        }
      : skipToken
  )

  const guest = getFragment(SharedAPI.guest.GUEST_FRAGMENT, data?.getGuest)

  const { errors, create, update } = useGuestForm({ id, onDone })

  return (
    <Form.Root<FormData>
      onSubmit={guest ? update : create}
      serverErrors={errors}
      readOnly={!canUpdate}
      defaultValues={{
        email: guest?.email ?? '',
        gender: GraphQL.Gender.NotSpecified,
        firstName: guest?.firstName ?? '',
        lastName: guest?.lastName ?? defaultName ?? '',
        title: guest?.title ?? ''
      }}
    >
      <div className="flex flex-col gap-5 p-2.5">
        <div className="flex flex-col pb-3 gap-6">
          <Form.Field
            name="title"
            label={`${t`Title`}`}
            rules={{
              maxLength: {
                value: 255,
                message: t`Can't be longer than 255 characters.`
              }
            }}
          >
            {({ renderProps: { onChange, ...props } }) => (
              <Input
                onChange={event => onChange(event.target.value)}
                type="text"
                placeholder=""
                {...props}
              />
            )}
          </Form.Field>
          <div className="flex flex-row gap-2 items-start">
            <Form.Field
              name="firstName"
              label={`${t`Given Name`}*`}
              rules={{
                maxLength: {
                  value: 255,
                  message: t`Can't be longer than 255 characters.`
                }
              }}
            >
              {({ renderProps: { onChange, ...props } }) => (
                <Input
                  onChange={event => onChange(event.target.value)}
                  type="text"
                  placeholder=""
                  {...props}
                />
              )}
            </Form.Field>
            <Form.Field
              name="lastName"
              label={`${t`Last Name`}*`}
              rules={{
                maxLength: {
                  value: 255,
                  message: t`Can't be longer than 255 characters.`
                },
                validate: {
                  required: Form.Validations.requiredWithTrimValidation(
                    t`This field is required`
                  )
                }
              }}
            >
              {({ renderProps: { onChange, ...props } }) => (
                <Input
                  onChange={event => onChange(event.target.value)}
                  type="text"
                  placeholder=""
                  {...props}
                />
              )}
            </Form.Field>
          </div>
          <Form.Field
            name="email"
            label={t`Email`}
            rules={{
              validate: {
                emailValidation: Form.Validations.emailValidation(
                  t`Not a valid email.`
                )
              }
            }}
          >
            {({ renderProps: { onChange, ...props } }) => (
              <Input
                type="text"
                Icon={Mail01Icon}
                onChange={event => onChange(event.target.value)}
                placeholder=""
                autoComplete="email webauthn work"
                {...props}
              />
            )}
          </Form.Field>
        </div>
        <footer className="flex flex-row items-center justify-end gap-4">
          <Button variant="outline" onClick={onDone} type="button">
            <Trans>Cancel</Trans>
          </Button>
          <Form.Submit>
            <Trans>Save</Trans>
          </Form.Submit>
        </footer>
      </div>
    </Form.Root>
  )
}
